import { useCallback, useMemo, useState } from 'react';

import { useNavigate } from 'react-router-dom';

import { useAppSelector } from '../../redux/hooks';
import { useMedications } from './hooks';
import useDeleteMedication from '../../hooks/queries/useDeleteMedication';

import MedicationComponent from './component';
import MedicationItem from './MedicationItem';

import { IMedication } from './types';
import { MEDICATION_DETAILS_FIELDS } from '../../constants/medication';
import { TSelectedMedication } from '../../constants/types';

const Medication: React.FC = () => {
  const [isAddMedicationModalOpen, setIsAddMedicationModalOpen] =
    useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [selectedMedicationId, setSelectedMedicationId] = useState<
    number | null
  >(null);
  const [selectedMedication, setSelectedMedication] =
    useState<IMedication | null>(null);

  const currentUser = useAppSelector((state) => state.auth.currentUser);
  const navigate = useNavigate();
  const { mutate: deleteMedication } = useDeleteMedication();
  const { medications } = useMedications({ patient_id: currentUser?.id });

  const toggleAddMedicationModal = () =>
    setIsAddMedicationModalOpen((prev) => !prev);
  const toggleDeleteModal = () => setIsDeleteModalOpen((prev) => !prev);
  const togglePreviewModal = () => setIsPreviewModalOpen((prev) => !prev);

  const handleBackNavigation = () => navigate('/tasks');

  const handleMedicationDelete = () => {
    if (selectedMedicationId) {
      deleteMedication(selectedMedicationId);
      toggleDeleteModal();
    }
  };

  const openPreviewModal = useCallback((medication: IMedication) => {
    setSelectedMedication(medication);
    togglePreviewModal();
  }, []);

  const openDeleteModal = useCallback((medicationId: number) => {
    setSelectedMedicationId(medicationId);
    toggleDeleteModal();
  }, []);

  const medicationList = useMemo(
    () =>
      medications?.map((medication: IMedication) => (
        <MedicationItem
          key={medication.id}
          medication={medication}
          onPreviewOpen={openPreviewModal}
          onDeleteOpen={openDeleteModal}
        />
      )),
    [medications, openPreviewModal, openDeleteModal]
  );

  const formatSelectedMedicationDetails = useMemo(() => {
    if (!selectedMedication) return {} as TSelectedMedication;

    return Object.keys(MEDICATION_DETAILS_FIELDS).reduce((acc, key) => {
      const fieldValue = selectedMedication[key as keyof IMedication];
      if (fieldValue !== undefined) {
        acc[MEDICATION_DETAILS_FIELDS[key] as string] = fieldValue;
      }
      return acc;
    }, {} as TSelectedMedication);
  }, [selectedMedication]);

  return (
    <MedicationComponent
      isAddMedicationModalOpen={isAddMedicationModalOpen}
      isDeleteModalOpen={isDeleteModalOpen}
      isPreviewModalOpen={isPreviewModalOpen}
      medicationList={medicationList}
      selectedMedication={formatSelectedMedicationDetails}
      setIsAddMedicationModalOpen={setIsAddMedicationModalOpen}
      setIsDeleteModalOpen={setIsDeleteModalOpen}
      setIsPreviewModalOpen={setIsPreviewModalOpen}
      handleDeleteMedication={handleMedicationDelete}
      handleAddMedication={toggleAddMedicationModal}
      handleBackNavigation={handleBackNavigation}
    />
  );
};

export default Medication;
